import React, { createContext, useContext, useState } from 'react';
import {
  getLanguageLocale,
  isSupportedLanguage,
  normalizeLanguage,
  translations,
  type Language
} from './translations';

export type TOptions = {
  defaultValue?: string;
  returnObjects?: boolean;
  [key: string]: any;
};

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, options?: TOptions) => any;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// 检测浏览器语言
const STORAGE_KEY = 'sheetmate_language_preference';

const detectLanguage = (): Language => {
  if (typeof navigator === 'undefined') {
    return 'en';
  }
  return normalizeLanguage(navigator.language);
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getInitialLanguage = (): Language => {
    if (typeof window !== 'undefined') {
      const stored = window.localStorage?.getItem(STORAGE_KEY);
      if (isSupportedLanguage(stored)) {
        return stored;
      }
    }
    return detectLanguage();
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, lang);
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = getLanguageLocale(lang);
    }
  };

  const t = (key: string, options?: TOptions) => {
    const keys = key.split('.');
    let value: any = translations[language];
    let fallbackValue: any = translations.en;
    
    for (const k of keys) {
      value = value?.[k];
      fallbackValue = fallbackValue?.[k];
      if (value === undefined || value === null) {
        value = fallbackValue;
      }
      if (value === undefined || value === null) {
        return options?.defaultValue || key;
      }
    }
    
    // 如果结果是对象且returnObjects选项不为true，返回警告或键名
    if (typeof value === 'object' && !options?.returnObjects) {
      console.warn(`Translation key "${key}" returned an object. Set returnObjects: true in options to return the object.`);
      return options?.defaultValue || key;
    }
    
    if (typeof value === 'string' && options) {
      return value.replace(/\{\{(\w+)\}\}/g, (_, optionKey: string) => {
        const replacement = options[optionKey];
        return replacement === undefined || replacement === null ? '' : String(replacement);
      });
    }

    return value;
  };

  React.useEffect(() => {
    document.documentElement.lang = getLanguageLocale(language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 
